import { Action, DataContainer } from '@otto-ec/tracking-bct';
import { ClientComponentLogger, LogErrorParams } from '../../client-component-logger';
import { TrackData, TrackingFeatures } from '../../tracking/client/event-data-types';
import { getGlobalEventMergeId, trackAddActionToEvent, trackEvent, trackMove, trackMoveAction, trackViewFeatures } from './tracking';

export abstract class BaseComponent2 {
    public abstract rootSelector: string;
    public rootElement?: HTMLElement;

    protected logger?: ClientComponentLogger;

    /**
     * this method runs only one time for this type of component
     * -WARNING-
     * the method is unrelated to the (number of) instances of the component
     * you cant use rootElement in this method
     */
    public async onFirstPageLoadOncePerComponentType(): Promise<unknown> {
        return;
    }

    /**
     * this method runs one time per instance of the class
     * also if RELOAD_COMPONENT_TOPIC is emitted AND new instance of this component is created the init method runs in the new instance
     */
    public async init(): Promise<unknown> {
        return;
    }

    /**
     * will be called whenever RELOAD_COMPONENT_TOPIC is emitted
     * but only, if the same instance of the components remains intact
     *
     * !WARNING!
     * This method is only needed in rare cases - if the component has to do logic after some dom elements have been reloaded
     */
    public async onReloadComponentEvent(): Promise<unknown> {
        return;
    }

    public async setLogger(logger: ClientComponentLogger) {
        this.logger = logger;
    }

    public setRootElement(element: HTMLElement) {
        this.rootElement = element;
    }

    /**
     * @returns root element of component
     */
    protected getRootElement(): HTMLElement {
        if (!this.rootElement) {
            throw new Error(`rootElement for '${this.rootSelector}' was not set`);
        }
        return this.rootElement;
    }

    /**
     * @param selector - query selector
     * @returns child element of component
     */
    protected getChildElement(selector: string): HTMLElement | undefined {
        return this.rootElement?.querySelector(selector) || undefined;
    }

    /**
     * @param selector query selectors
     * @returns array of child elements of component
     */
    protected getChildElements(selector: string): HTMLElement[] {
        return Array.from(this.getRootElement().querySelectorAll(selector));
    }

    /**
     * @returns `false` if component is not present in document, `true` otherwise
     */
    protected isPresent(): boolean {
        return this.rootElement ? document.body.contains(this.rootElement) : false;
    }

    /**
     * hides the component
     */
    protected hide() {
        this.getRootElement().classList.add('pl_hidden');
    }

    /**
     * makes the component visible
     */
    protected show() {
        this.getRootElement().classList.remove('pl_hidden');
    }

    /**
     * hides children the component
     */
    protected hideChild(selector: string) {
        const element = this.getChildElement(selector);
        element?.classList.add('pl_hidden');
    }

    /**
     * makes children the component visible
     */
    protected showChild(selector: string) {
        const element = this.getChildElement(selector);
        element?.classList.remove('pl_hidden');
    }

    /**
     * logs errors to server and in console
     * @param params error data to log
     * never throws any error
     */
    protected logError(params: LogErrorParams) {
        this.logger?.logError(params) || console.error(params);
    }

    /**
     * tracks a submit event
     * @param data any data passed to click tracking
     */
    public trackEvent(data: Record<string, unknown>): void {
        trackEvent(data);
    }

    /**
     * use this method to track a view
     * @param data any data passed to view tracking
     */
    public trackViewFeatures(data: TrackingFeatures): void {
        if (o_product.state.isVariationChanged) {
            const eventMergeId = getGlobalEventMergeId();
            if (eventMergeId === undefined) {
                throw new Error('No global event merge id is set after variation change, cannot merge action onto variation change event');
            }
            trackAddActionToEvent({ name: 'change', features: data }, eventMergeId);
        } else {
            trackViewFeatures(data);
        }
    }

    public trackMove(data: TrackData): void {
        trackMove(data);
    }

    public trackMoveAction(data: DataContainer, action: Action): void {
        trackMoveAction(data, action);
    }
}
